/**
 * Panel del organizador (admin).
 *
 * Mantiene la lógica de datos del archivo original (fetch /admin/:slug,
 * analytics, orders, stripe connect, porra). El rediseño cambia solo
 * presentación y copy:
 *   - Header con título + estado (badge con dot).
 *   - Compartir (copiar / WhatsApp).
 *   - 4 KPIs: pagados (c/barra), recaudado, por cobrar, cierre.
 *   - "Resumen del dinero" (recaudado − comisión = recibirás).
 *   - StripeConnectPanel (verde "Tu cuenta está lista" si listo).
 *   - Tabla "Quién ha pagado" con badges y botón Recordar.
 *   - Velocidad 7d en barras oliva sólidas (no gradiente).
 *   - Disclosure "Acciones avanzadas".
 */
import { useEffect, useState, useCallback } from 'react';
import { useParams, useSearchParams, Link } from 'react-router-dom';
import api from '../api/client';
import toast from 'react-hot-toast';
import OnboardingTour from '../components/OnboardingTour';
import ReceiptScanner from '../components/ReceiptScanner';
import ParticipantsManager from '../components/ParticipantsManager';
import TopBar from './../components/TopBar';
import {
  copyFor,
  statusLabel,
  statusDescription,
  incompletePolicyLabel,
} from '../utils/vertical-copy';


interface GroupBuyAdmin {
  id: string;
  slug: string;
  admin_token: string;
  type: string;
  title: string;
  description?: string;
  organizer_name: string;
  organizer_email: string;
  organizer_phone?: string;
  amount_per_share?: string;
  amount_total_target?: string;
  expected_shares?: number;
  min_shares?: number;
  max_shares?: number;
  deadline?: string;
  on_incomplete: string;
  status: string;
  paid_shares_count: number;
  total_paid_amount: string;
  created_at: string;
  completed_at?: string;
  settled_at?: string;
  payment_mode?: string;
  organizer_already_paid?: boolean;
  lucky_draw_enabled?: boolean;
  custom_fields?: Record<string, any> | null;
}


// Mapa de estados → tokens (igual que en el README).
const STATUS_STYLE: Record<string, { dot: string; text: string; bg: string }> = {
  draft:     { dot: '#A89D8E', text: '#6E6557', bg: '#F0EBE2' },
  open:      { dot: '#5E7B3E', text: '#46602C', bg: '#E8EEDC' },
  completed: { dot: '#1C5D82', text: '#154B6B', bg: '#E6EEF3' },
  expired:   { dot: '#BE841B', text: '#8A5F12', bg: '#F7EBD2' },
  cancelled: { dot: '#B0392C', text: '#8A2C22', bg: '#F6E0DC' },
  settled:   { dot: '#15506B', text: '#103B50', bg: '#E2EBF0' },
  paid:      { dot: '#5E7B3E', text: '#46602C', bg: '#E8EEDC' },
  pending:   { dot: '#BE841B', text: '#8A5F12', bg: '#F7EBD2' },
  refunded:  { dot: '#6E7C86', text: '#4C565E', bg: '#E7ECEF' },
};


function formatEUR(n: number): string {
  return n.toLocaleString('es-ES', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + ' €';
}


export default function GroupBuyAdminPage() {
  const { slug } = useParams<{ slug: string }>();
  const [params] = useSearchParams();
  const adminToken = params.get('t') || '';
  const [gb, setGb] = useState<GroupBuyAdmin | null>(null);
  const [loading, setLoading] = useState(true);
  const [participantsRefreshKey, setParticipantsRefreshKey] = useState(0);

  const refresh = useCallback(() => {
    if (!slug || !adminToken) return;
    setLoading(true);
    api.get(`/public/group-buys/admin/${slug}?t=${adminToken}`)
      .then((r) => setGb(r.data))
      .catch(() => toast.error('Sin acceso o cobro no encontrado'))
      .finally(() => setLoading(false));
  }, [slug, adminToken]);

  useEffect(() => { refresh(); }, [refresh]);

  const publicUrl = `${window.location.origin}/g/${slug}`;

  const copyPublic = () => {
    navigator.clipboard.writeText(publicUrl);
    toast.success('Enlace copiado al portapapeles');
  };

  const generateWhatsAppMessage = () => {
    if (!gb) return '';
    const c = copyFor(gb.type);
    const amount = gb.amount_per_share ? formatEUR(parseFloat(gb.amount_per_share)) : '';
    return encodeURIComponent(
      `Hola peña\n\nHe creado un cobro para *${gb.title}*${amount ? ` (${amount} cada ${c.payerSingular})` : ''}.\n\nCada uno paga su parte aquí:\n${publicUrl}\n\nGracias.`,
    );
  };

  const cancelGroupBuy = async () => {
    if (!gb || !confirm('¿Cancelar este cobro? Lo que ya esté pagado se procesará según la política que elegiste.')) return;
    try {
      await api.post(`/public/group-buys/admin/${slug}/cancel?t=${adminToken}`);
      toast.success('Cobro cancelado');
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No hemos podido cancelarlo');
    }
  };

  if (loading)
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface text-ink-500 font-sans">
        Un momento…
      </div>
    );
  if (!gb)
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface px-6 text-center font-sans">
        <div className="max-w-sm">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · Acceso restringido
          </div>
          <h1 className="font-display font-bold text-[28px] text-ink mb-2 tracking-[-0.02em]">
            Este enlace de organizador no es válido
          </h1>
          <p className="text-ink-700 text-[14.5px]">
            Pídele al creador del cobro que te lo reenvíe.
          </p>
        </div>
      </div>
    );

  const c = copyFor(gb.type);
  const totalPaid = parseFloat(gb.total_paid_amount);
  const amountPerShare = gb.amount_per_share ? parseFloat(gb.amount_per_share) : null;
  const expectedTotal = amountPerShare && gb.expected_shares ? amountPerShare * gb.expected_shares : null;
  const progressPct = gb.expected_shares ? (gb.paid_shares_count / gb.expected_shares) * 100 : 0;
  const statusStyle = STATUS_STYLE[gb.status] || STATUS_STYLE.draft;

  return (
    <div className="min-h-screen bg-surface text-ink font-sans antialiased">
      <OnboardingTour slug={slug!} />

      <TopBar contextLabel="Cobro · admin" rightCta={{ label: 'Mis cobros', to: '/mis-cobros' }} />

      <header className="bg-paper border-b border-line">
        <div className="max-w-5xl mx-auto px-6 py-7 md:py-9">
          <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
            · {c.shortLabel} · panel del organizador
          </div>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3">
            <div>
              <h1 className="font-display font-bold text-[34px] md:text-[40px] leading-[1.04] tracking-[-0.02em] text-ink mb-1">
                {gb.title}
              </h1>
              <p className="text-[13.5px] text-ink-500">
                Organiza:{' '}
                <span className="text-ink-700">{gb.organizer_name}</span> ·{' '}
                <span className="text-ink-700">{gb.organizer_email}</span>
              </p>
            </div>
            <StatusBadge status={gb.status} />
          </div>
        </div>
      </header>

      <main className="max-w-5xl mx-auto px-6 py-8 space-y-6">
        {/* Compartir */}
        <Card eyebrow="Compartir con el grupo">
          <p className="text-[13.5px] text-ink-700 mb-4">
            Pega este enlace en el WhatsApp del grupo y cada uno paga su parte.
          </p>
          <div className="flex flex-col md:flex-row gap-2">
            <div className="flex-1 bg-surface border border-line rounded-xl px-4 py-3 font-mono text-[13px] text-ink-700 break-all">
              {publicUrl}
            </div>
            <button
              onClick={copyPublic}
              className="px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14px] shadow-btn hover:bg-brand-700 transition"
            >
              Copiar
            </button>
            <a
              href={`https://wa.me/?text=${generateWhatsAppMessage()}`}
              target="_blank"
              rel="noreferrer"
              className="px-5 py-3 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 font-semibold text-[14px] hover:border-brand text-center transition"
            >
              WhatsApp
            </a>
          </div>
        </Card>

        {/* 4 KPIs */}
        <section className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <Kpi
            eyebrow={`${c.payerPlural} que pagaron`}
            value={
              <span className="font-display font-bold text-[34px] text-ink leading-none tracking-[-0.02em]">
                {gb.paid_shares_count}
                {gb.expected_shares && (
                  <span className="text-ink-400 text-[22px] font-semibold"> / {gb.expected_shares}</span>
                )}
              </span>
            }
            after={
              gb.expected_shares ? (
                <div className="mt-3 h-2 bg-line rounded-pill overflow-hidden">
                  <div
                    className="h-full bg-olive rounded-pill"
                    style={{ width: `${Math.min(100, progressPct)}%` }}
                  />
                </div>
              ) : null
            }
          />
          <Kpi
            eyebrow="Recaudado"
            value={
              <span className="font-display font-bold text-[30px] text-olive-700 leading-none tracking-[-0.02em]">
                {formatEUR(totalPaid)}
              </span>
            }
            after={
              expectedTotal != null ? (
                <p className="mt-2 text-[12px] text-ink-500">
                  de {formatEUR(expectedTotal)} esperados
                </p>
              ) : null
            }
          />
          <PendingKpi slug={slug!} token={adminToken} />
          <Kpi
            eyebrow="Cierre"
            value={
              <span className="font-display font-bold text-[24px] text-ink leading-tight tracking-[-0.01em]">
                {gb.deadline
                  ? new Date(gb.deadline).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' })
                  : 'Sin fecha'}
              </span>
            }
            after={
              <p
                className="mt-2 text-[12px] text-ink-500"
                title={incompletePolicyLabel(gb.on_incomplete)}
              >
                Si no llegáis: {incompletePolicyLabel(gb.on_incomplete).toLowerCase()}
              </p>
            }
          />
        </section>

        {/* Resumen del dinero + analítica */}
        <AnalyticsPanel slug={slug!} token={adminToken} typeCopy={c.payerPlural} />

        {/* OCR + Participantes (solo si payment_mode=uneven) */}
        {gb.payment_mode === 'uneven' && (
          <div className="space-y-4">
            <ReceiptScanner
              slug={slug!}
              adminToken={adminToken}
              onSplitDone={() => setParticipantsRefreshKey((k) => k + 1)}
            />
            <ParticipantsManager
              slug={slug!}
              adminToken={adminToken}
              refreshKey={participantsRefreshKey}
            />
          </div>
        )}

        {/* Oficina Fácil · Mis facturas + logo + CTA Hub del autónomo */}
        {gb.custom_fields?.freelance_invoice && (
          <>
            <InvoicesPanel slug={slug!} token={adminToken} />
            <OfficinaFacilHubCTA organizerEmail={gb.organizer_email} />
          </>
        )}

        {/* Procesador de pago · Pagafácil Pay (Stripe Connect) o TPV propio del organizador */}
        <PaymentProcessorSection slug={slug!} token={adminToken} />

        {/* Idea 3 ROADMAP · junta directiva con magic links */}
        <TeamPanel slug={slug!} token={adminToken} />

        {/* Idea 12 ROADMAP · categorías deportivas con importes distintos */}
        <CategoryTiersPanel slug={slug!} token={adminToken} />

        {/* Lista de pagadores */}
        <OrdersList slug={slug!} token={adminToken} />

        {/* Porra */}
        {gb.type === 'porra' && (gb.status === 'open' || gb.status === 'completed') && (
          <PorraResultForm slug={slug!} token={adminToken} onSettled={refresh} />
        )}
        {gb.type === 'porra' && <PorraRankingPanel slug={slug!} />}

        {/* Idea 13 ROADMAP · panel dedicado de caseta */}
        {gb.type === 'caseta' && (
          <section className="bg-terra-50 border border-[#E6CDB6] rounded-card p-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-terra-700 mb-2">
              · Caseta de feria
            </div>
            <h2 className="font-display font-semibold text-[20px] text-terra-700 tracking-[-0.01em] mb-2">
              Gestión de bonos, mesas e invitados
            </h2>
            <p className="text-[13.5px] text-ink-700 mb-4">
              Panel dedicado con saldo prepago en barra, reservas con estado y check-in de invitados.
            </p>
            <a
              href={`/c/${slug}/admin?t=${adminToken}`}
              className="inline-block px-5 py-3 rounded-xl bg-terra text-paper font-semibold text-[14px] shadow-btn hover:opacity-90 transition"
            >
              Abrir panel de caseta →
            </a>
          </section>
        )}

        {/* PDF de cierre · disponible cuando el cobro está cerrado */}
        {(gb.status === 'completed' || gb.status === 'settled' || gb.status === 'cancelled') && (
          <Card eyebrow="Rendición de cuentas">
            <p className="text-[14px] text-ink-700 mb-4">
              Descarga el PDF formal de cierre con totales, lista de pagados,
              pendientes y acuse de liquidación. Tu junta puede archivarlo como
              comprobante auditable con huella SHA-256.
            </p>
            <a
              href={`/api/public/group-buys/admin/${slug}/cierre.pdf?t=${adminToken}`}
              className="inline-block px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14px] shadow-btn hover:bg-brand-700 transition"
              target="_blank"
              rel="noopener noreferrer"
            >
              Descargar PDF de cierre ↓
            </a>
          </Card>
        )}

        {/* Acciones avanzadas */}
        {(gb.status === 'open' || gb.status === 'completed') && (
          <details className="text-sm pt-2">
            <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 hover:text-ink">
              + Acciones avanzadas
            </summary>
            <div className="mt-4 bg-paper border border-line rounded-card p-5">
              <button
                onClick={cancelGroupBuy}
                className="px-4 py-2.5 rounded-xl border-[1.5px] border-[#E6B8B0] bg-brick-50 text-brick-700 text-[13.5px] font-semibold hover:bg-[#F2D3CC] transition"
              >
                Cancelar este cobro
              </button>
              <p className="text-[12.5px] text-ink-500 mt-3">
                Se aplicará la política{' '}
                <strong className="text-ink-700">
                  {incompletePolicyLabel(gb.on_incomplete).toLowerCase()}
                </strong>
                .
              </p>
            </div>
          </details>
        )}
      </main>
    </div>
  );
}


function StatusBadge({ status }: { status: string }) {
  const s = STATUS_STYLE[status] || STATUS_STYLE.draft;
  return (
    <span
      className="inline-flex items-center gap-2 px-3 py-1.5 rounded-pill text-[12px] font-bold whitespace-nowrap"
      style={{ background: s.bg, color: s.text }}
      title={statusDescription(status)}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
      {statusLabel(status)}
    </span>
  );
}


function Card({
  eyebrow,
  children,
}: {
  eyebrow?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-paper border border-line shadow-card rounded-card p-6 md:p-7">
      {eyebrow && (
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
          · {eyebrow}
        </div>
      )}
      {children}
    </section>
  );
}


function Kpi({
  eyebrow,
  value,
  after,
}: {
  eyebrow: string;
  value: React.ReactNode;
  after?: React.ReactNode;
}) {
  return (
    <div className="bg-paper border border-line shadow-card rounded-card p-5">
      <div className="font-mono text-[10.5px] uppercase tracking-[0.16em] text-ink-500 mb-2">
        · {eyebrow}
      </div>
      {value}
      {after}
    </div>
  );
}


function PendingKpi({ slug, token }: { slug: string; token: string }) {
  // Lee de analytics para mostrar "por cobrar" (revenue.pending_revenue).
  const [pending, setPending] = useState<number | null>(null);
  const [count, setCount] = useState<number | null>(null);

  useEffect(() => {
    api
      .get(`/public/group-buys/admin/${slug}/analytics?t=${token}`)
      .then((r) => {
        setPending(r.data.revenue?.pending_revenue ?? 0);
        setCount(r.data.funnel?.pending ?? 0);
      })
      .catch(() => {});
  }, [slug, token]);

  return (
    <Kpi
      eyebrow="Por cobrar"
      value={
        <span className="font-display font-bold text-[30px] text-amberCC-700 leading-none tracking-[-0.02em]">
          {pending != null ? formatEUR(pending) : '—'}
        </span>
      }
      after={
        count != null && count > 0 ? (
          <p className="mt-2 text-[12px] text-ink-500">{count} entraron sin pagar</p>
        ) : null
      }
    />
  );
}


function AnalyticsPanel({
  slug,
  token,
  typeCopy,
}: {
  slug: string;
  token: string;
  typeCopy: string;
}) {
  const [data, setData] = useState<any>(null);

  useEffect(() => {
    api.get(`/public/group-buys/admin/${slug}/analytics?t=${token}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [slug, token]);

  if (!data) return null;

  const paid = data.revenue?.paid_revenue ?? 0;
  const fee = data.revenue?.platform_fee_estimate ?? 0;
  const net = paid - fee;
  const maxVelocity = Math.max(...(data.velocity_last_7d || []).map((d: any) => d.count), 1);

  return (
    <Card eyebrow="Resumen del dinero">
      {/* Recaudado − comisión = recibirás */}
      <div className="grid grid-cols-3 gap-4 mb-2 text-center">
        <div>
          <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 mb-1">
            Recaudado
          </div>
          <div className="font-display font-bold text-[24px] text-ink leading-none tracking-[-0.02em]">
            {formatEUR(paid)}
          </div>
        </div>
        <div>
          <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 mb-1">
            Comisión
          </div>
          <div className="font-display font-bold text-[24px] text-terra-700 leading-none tracking-[-0.02em]">
            − {formatEUR(fee)}
          </div>
        </div>
        <div>
          <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 mb-1">
            Recibirás
          </div>
          <div className="font-display font-bold text-[24px] text-olive-700 leading-none tracking-[-0.02em]">
            {formatEUR(net)}
          </div>
        </div>
      </div>
      <p className="mt-3 text-[12px] text-ink-500 text-center">
        El pagador no ve la comisión · sale de lo recaudado.
      </p>

      {data.funnel && data.funnel.joined > 0 && (
        <>
          <div className="border-t border-line-soft mt-6 pt-6 grid grid-cols-2 md:grid-cols-3 gap-4 text-[13px]">
            <div className="bg-surface border border-line rounded-xl p-3">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500">
                Llegan a pagar
              </div>
              <div className="font-display font-bold text-[22px] text-brand leading-tight tracking-[-0.01em]">
                {data.funnel.conversion_rate}%
              </div>
              <div className="text-[12px] text-ink-500">
                {data.funnel.paid} de los {data.funnel.joined} que entraron
              </div>
            </div>
            <div className="bg-surface border border-line rounded-xl p-3">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500">
                Tardan en pagar
              </div>
              <div className="font-display font-bold text-[22px] text-ink leading-tight tracking-[-0.01em]">
                {formatMinutes(data.time_to_pay.median_minutes)}
              </div>
              <div className="text-[12px] text-ink-500">desde que entran</div>
            </div>
            <div className="bg-surface border border-line rounded-xl p-3 col-span-2 md:col-span-1">
              <div className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500">
                {typeCopy} pendientes
              </div>
              <div className="font-display font-bold text-[22px] text-amberCC-700 leading-tight tracking-[-0.01em]">
                {data.funnel.pending}
              </div>
              <div className="text-[12px] text-ink-500">aún no han completado</div>
            </div>
          </div>

          <div className="border-t border-line-soft mt-6 pt-6">
            <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 mb-3">
              · Pagos últimos 7 días
            </div>
            <div className="flex items-end gap-1 h-24">
              {(data.velocity_last_7d || []).map((d: any) => (
                <div key={d.date} className="flex-1 flex flex-col items-center">
                  <div className="text-[11px] text-ink-500 mb-1">
                    {d.count > 0 ? d.count : ''}
                  </div>
                  <div
                    className="w-full bg-olive rounded-t-[3px]"
                    style={{
                      height: `${(d.count / maxVelocity) * 100}%`,
                      minHeight: d.count > 0 ? '4px' : '0',
                    }}
                  />
                  <div className="text-[10.5px] text-ink-400 mt-1 font-mono">
                    {new Date(d.date).getDate()}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </Card>
  );
}


function formatMinutes(min: number): string {
  if (min < 1) return '< 1 min';
  if (min < 60) return `${Math.round(min)} min`;
  const hours = Math.round((min / 60) * 10) / 10;
  if (hours < 24) return `${hours} h`;
  return `${Math.round(hours / 24)} d`;
}


function CategoryTiersPanel({ slug, token }: { slug: string; token: string }) {
  const [tiers, setTiers] = useState<{ name: string; amount: number }[] | null>(null);
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => {
    api.get(`/public/group-buys/admin/${slug}/category-tiers?t=${token}`)
      .then((r) => setTiers(r.data.tiers || []))
      .catch(() => setTiers([]));
  }, [slug, token]);

  useEffect(() => { refresh(); }, [refresh]);

  if (tiers === null) return null;

  const save = async (next: any[]) => {
    setSaving(true);
    try {
      await api.put(`/public/group-buys/admin/${slug}/category-tiers?t=${token}`, {
        tiers: next,
      });
      toast.success(next.length ? 'Categorías guardadas' : 'Categorías eliminadas');
      setTiers(next);
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo guardar');
    } finally {
      setSaving(false);
    }
  };

  const addTier = () => save([...(tiers || []), { name: '', amount: 0 }]);
  const updateTier = (i: number, field: 'name' | 'amount', value: any) => {
    const next = [...(tiers || [])];
    next[i] = { ...next[i], [field]: field === 'amount' ? parseFloat(value) || 0 : value };
    setTiers(next);
  };
  const persist = () => save(tiers || []);
  const removeTier = (i: number) => save((tiers || []).filter((_, idx) => idx !== i));

  return (
    <Card eyebrow="Categorías con importes">
      <p className="text-[13.5px] text-ink-700 mb-3">
        Para clubes deportivos · cuotas distintas por categoría (sub-12, sub-14, sénior…).
        Si pones categorías, cada pagador elige una al unirse y el importe viene de aquí.
      </p>

      {tiers.length === 0 ? (
        <button
          type="button"
          onClick={addTier}
          className="px-4 py-2 rounded-xl border-[1.5px] border-dashed border-line-strong text-ink-700 text-[13px] font-semibold hover:border-brand hover:text-brand transition"
        >
          + Añadir primera categoría
        </button>
      ) : (
        <div className="space-y-2">
          {tiers.map((tier, i) => (
            <div key={i} className="grid grid-cols-[1fr_140px_auto] gap-2">
              <input
                type="text"
                value={tier.name}
                onChange={(e) => updateTier(i, 'name', e.target.value)}
                placeholder="Nombre · sub-12"
                className="cc-input"
              />
              <input
                type="number"
                step="0.01"
                value={tier.amount}
                onChange={(e) => updateTier(i, 'amount', e.target.value)}
                placeholder="0,00"
                className="cc-input"
              />
              <button
                type="button"
                onClick={() => removeTier(i)}
                className="text-ink-400 hover:text-brick px-2 text-[18px]"
                aria-label="Quitar"
              >
                ×
              </button>
            </div>
          ))}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={addTier}
              className="px-3 py-1.5 rounded-xl border-[1.5px] border-line-strong text-ink-700 text-[12.5px] font-semibold hover:border-brand transition"
            >
              + Añadir
            </button>
            <button
              type="button"
              onClick={persist}
              disabled={saving}
              className="px-3 py-1.5 rounded-xl bg-brand text-paper text-[12.5px] font-semibold hover:bg-brand-700 transition disabled:opacity-60"
            >
              {saving ? 'Guardando…' : 'Guardar cambios'}
            </button>
          </div>
        </div>
      )}
    </Card>
  );
}


function TeamPanel({ slug, token }: { slug: string; token: string }) {
  const [members, setMembers] = useState<any[] | null>(null);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [role, setRole] = useState('vocal');
  const [email, setEmail] = useState('');
  const [saving, setSaving] = useState(false);

  const refresh = useCallback(() => {
    api.get(`/public/group-buys/admin/${slug}/team?t=${token}`)
      .then((r) => setMembers(r.data.items || []))
      .catch(() => setMembers([]));
  }, [slug, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const invite = async () => {
    if (!name.trim()) {
      toast.error('Pon un nombre');
      return;
    }
    setSaving(true);
    try {
      const { data } = await api.post(`/public/group-buys/admin/${slug}/team?t=${token}`, {
        name: name.trim(),
        role,
        email: email.trim() || null,
      });
      toast.success(`${data.name} añadido a la junta`);
      setName('');
      setEmail('');
      setOpen(false);
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo añadir');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!confirm('¿Quitar este miembro?')) return;
    try {
      await api.delete(`/public/group-buys/admin/${slug}/team/${id}?t=${token}`);
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo quitar');
    }
  };

  const copyLink = (url: string) => {
    const fullUrl = `${window.location.origin}${url}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      toast.success('Enlace copiado');
    }).catch(() => {
      toast.error('No se pudo copiar');
    });
  };

  if (members === null) return null;

  return (
    <Card eyebrow="Junta directiva">
      <div className="flex items-baseline justify-between mb-3">
        <p className="text-[13.5px] text-ink-700">
          Comparte el panel con tu junta · cada miembro recibe el mismo enlace de organizador.
        </p>
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          className="font-mono text-[11px] uppercase tracking-[0.18em] text-brand hover:text-brand-700"
        >
          {open ? '×' : '+ Invitar'}
        </button>
      </div>

      {open && (
        <div className="mb-4 bg-surface border border-line rounded-xl p-4 space-y-2">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Nombre"
              className="cc-input"
            />
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="cc-input"
            >
              <option value="presidente">Presidente</option>
              <option value="vicepresidente">Vicepresidente</option>
              <option value="tesorero">Tesorero</option>
              <option value="secretario">Secretario</option>
              <option value="vocal">Vocal</option>
              <option value="hermano_mayor">Hermano mayor</option>
              <option value="delegado">Delegado</option>
              <option value="otro">Otro</option>
            </select>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email (opcional)"
              className="cc-input"
            />
          </div>
          <button
            type="button"
            onClick={invite}
            disabled={saving}
            className="px-4 py-2 rounded-xl bg-brand text-paper font-semibold text-[13px] hover:bg-brand-700 transition disabled:opacity-60"
          >
            {saving ? 'Guardando…' : 'Añadir a la junta'}
          </button>
        </div>
      )}

      {members.length === 0 ? (
        <p className="text-[12.5px] text-ink-500">
          Aún solo tú. Invita a tesorero/secretario/presidente para compartir el panel.
        </p>
      ) : (
        <ul className="divide-y divide-line-soft">
          {members.map((m: any) => (
            <li key={m.id} className="py-2.5 flex items-baseline gap-3">
              <div className="flex-1 min-w-0">
                <div className="font-display font-semibold text-[14.5px] text-ink tracking-[-0.01em] truncate">
                  {m.name}
                </div>
                <div className="text-[12px] text-ink-500 truncate">
                  {m.role.replace('_', ' ')}
                  {m.email && ` · ${m.email}`}
                  {m.last_used_at && ` · último uso ${new Date(m.last_used_at).toLocaleDateString('es-ES')}`}
                </div>
              </div>
              <button
                type="button"
                onClick={() => copyLink(m.admin_url)}
                className="px-3 py-1.5 rounded-pill text-[11.5px] font-semibold border border-line-strong bg-paper text-ink-700 hover:border-brand hover:text-brand transition"
              >
                Copiar link
              </button>
              <button
                type="button"
                onClick={() => remove(m.id)}
                className="text-ink-400 hover:text-brick text-[16px]"
                aria-label="Quitar"
              >
                ×
              </button>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
}


function AttachmentToggle({
  slug,
  token,
  orderId,
  count,
  isOffline,
}: {
  slug: string;
  token: string;
  orderId: string;
  count: number;
  isOffline: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState<any[] | null>(null);
  const [uploading, setUploading] = useState(false);

  const load = useCallback(() => {
    api.get(`/public/group-buys/admin/${slug}/orders/${orderId}/attachments?t=${token}`)
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]));
  }, [slug, token, orderId]);

  useEffect(() => {
    if (open && items === null) load();
  }, [open, items, load]);

  const upload = async (file: File) => {
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Máximo 5 MB');
      return;
    }
    const caption = window.prompt('Descripción del comprobante (opcional)') || '';
    const fd = new FormData();
    fd.append('file', file);
    setUploading(true);
    try {
      await api.post(
        `/public/group-buys/admin/${slug}/orders/${orderId}/attachment?t=${token}&caption=${encodeURIComponent(caption)}`,
        fd,
        { headers: { 'Content-Type': 'multipart/form-data' } },
      );
      toast.success('Comprobante subido');
      setItems(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo subir');
    } finally {
      setUploading(false);
    }
  };

  const remove = async (attId: string) => {
    if (!confirm('¿Borrar este comprobante?')) return;
    try {
      await api.delete(
        `/public/group-buys/admin/${slug}/orders/${orderId}/attachment/${attId}?t=${token}`,
      );
      toast.success('Borrado');
      setItems(null);
      load();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo borrar');
    }
  };

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`inline-flex items-center gap-1 px-2.5 py-1.5 rounded-pill border text-[11.5px] font-semibold transition ${
          count > 0
            ? 'bg-brand-50 text-brand-700 border-[#CBD9E2] hover:bg-brand hover:text-paper'
            : 'bg-paper text-ink-500 border-line-strong hover:border-brand'
        }`}
        title={isOffline ? 'Comprobantes (Bizum manual, ticket, transferencia)' : 'Comprobantes adjuntos'}
      >
        📎 {count > 0 ? count : isOffline ? 'Adjuntar' : 'Adjuntar'}
      </button>
      {open && (
        <div className="absolute right-0 z-10 mt-1.5 w-[280px] bg-paper border border-line shadow-card rounded-card p-3 text-left">
          <div className="font-mono text-[10px] uppercase tracking-[0.14em] text-ink-500 mb-2">
            · Comprobantes
          </div>
          {items === null ? (
            <p className="text-[12px] text-ink-500">Un momento…</p>
          ) : items.length === 0 ? (
            <p className="text-[12px] text-ink-500 mb-2">Aún no hay ninguno.</p>
          ) : (
            <ul className="space-y-1.5 mb-3 max-h-[160px] overflow-y-auto">
              {items.map((it) => (
                <li key={it.id} className="flex items-start gap-2 text-[12px]">
                  <a
                    href={`${it.download_url}`}
                    target="_blank"
                    rel="noreferrer"
                    className="flex-1 text-brand hover:text-brand-700 truncate"
                    title={it.caption || it.filename}
                  >
                    {it.filename}
                  </a>
                  <button
                    type="button"
                    onClick={() => remove(it.id)}
                    className="text-ink-400 hover:text-brick text-[14px] leading-none"
                    aria-label="Borrar"
                  >
                    ×
                  </button>
                </li>
              ))}
            </ul>
          )}
          <label className="block">
            <input
              type="file"
              accept="image/*,application/pdf"
              capture="environment"
              onChange={(e) => e.target.files?.[0] && upload(e.target.files[0])}
              className="hidden"
              disabled={uploading}
            />
            <span
              className={`block text-center px-3 py-2 rounded-xl border-[1.5px] border-dashed text-[12px] font-semibold cursor-pointer transition ${
                uploading
                  ? 'border-ink-400 text-ink-400'
                  : 'border-line-strong text-ink-700 hover:border-brand hover:text-brand'
              }`}
            >
              {uploading ? 'Subiendo…' : '+ Subir foto o PDF (máx 5 MB)'}
            </span>
          </label>
        </div>
      )}
    </div>
  );
}


function OrdersList({ slug, token }: { slug: string; token: string }) {
  const [data, setData] = useState<any>(null);
  const [showOnlyIncidences, setShowOnlyIncidences] = useState(false);

  const refresh = useCallback(() => {
    api.get(`/public/group-buys/admin/${slug}/orders?t=${token}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [slug, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const markOffline = async (orderId: string) => {
    if (!confirm('Marcar este pago como pagado por Bizum manual o efectivo? · No cobramos comisión.')) return;
    try {
      await api.post(`/public/group-buys/admin/${slug}/orders/${orderId}/mark-offline?t=${token}`);
      toast.success('Marcado como pagado offline');
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo marcar');
    }
  };

  const undoOffline = async (orderId: string) => {
    try {
      await api.post(`/public/group-buys/admin/${slug}/orders/${orderId}/mark-unpaid?t=${token}`);
      toast.success('Vuelto a pendiente');
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo deshacer');
    }
  };

  const markIncidence = async (orderId: string) => {
    const note = prompt('Motivo de la incidencia (opcional)') || '';
    try {
      await api.post(
        `/public/group-buys/admin/${slug}/orders/${orderId}/mark-incidence?t=${token}&note=${encodeURIComponent(note)}`,
      );
      toast.success('Marcado para revisión');
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo marcar');
    }
  };

  const clearIncidence = async (orderId: string) => {
    try {
      await api.post(`/public/group-buys/admin/${slug}/orders/${orderId}/clear-incidence?t=${token}`);
      toast.success('Incidencia resuelta');
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo limpiar');
    }
  };

  if (!data || data.orders.length === 0) return null;

  const visibleOrders = showOnlyIncidences
    ? data.orders.filter((o: any) => o.has_incidence)
    : data.orders;

  const orderStatusLabel: Record<string, string> = {
    paid: 'Pagado',
    pending: 'Falta pagar',
    refunded: 'Devuelto',
    cancelled: 'Cancelado',
  };

  return (
    <Card eyebrow="Quién ha pagado">
      <div className="flex items-baseline justify-between mb-4 text-[12.5px] text-ink-500 font-mono uppercase tracking-[0.12em] flex-wrap gap-2">
        <span>
          <strong className="text-olive-700">{data.summary.paid}</strong> pagados ·{' '}
          <strong className="text-amberCC-700">{data.summary.pending}</strong> les falta
          {data.summary.refunded > 0 && (
            <>
              {' '}
              · <strong className="text-slateCC">{data.summary.refunded}</strong> devoluciones
            </>
          )}
        </span>
        {data.summary.paid_offline > 0 && (
          <span className="text-ink-500">
            · {data.summary.paid_online} online · {data.summary.paid_offline} offline (sin comisión)
          </span>
        )}
        {data.summary.incidences > 0 && (
          <button
            type="button"
            onClick={() => setShowOnlyIncidences((v) => !v)}
            className={`px-2 py-1 rounded-pill text-[11px] font-bold border transition ${
              showOnlyIncidences
                ? 'bg-amberCC text-paper border-amberCC'
                : 'bg-amberCC-50 text-amberCC-700 border-[#ECD9AE] hover:bg-amberCC hover:text-paper'
            }`}
            title="Filtrar por pagos con incidencia"
          >
            ⚠ {data.summary.incidences} incidencia{data.summary.incidences === 1 ? '' : 's'}
            {showOnlyIncidences && ' · quitar filtro'}
          </button>
        )}
      </div>
      <div className="overflow-x-auto -mx-6 px-6">
        <table className="w-full text-[13.5px]">
          <thead>
            <tr className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 border-b border-line">
              <th className="text-left py-2 font-normal">Nombre</th>
              <th className="text-left py-2 font-normal hidden md:table-cell">Email</th>
              <th className="text-center py-2 font-normal">Cant.</th>
              <th className="text-right py-2 font-normal">Importe</th>
              <th className="text-center py-2 font-normal">Estado</th>
              <th className="text-right py-2 font-normal">Acción</th>
            </tr>
          </thead>
          <tbody>
            {visibleOrders.map((o: any) => {
              const s = STATUS_STYLE[o.status] || STATUS_STYLE.pending;
              const rowBg = o.has_incidence ? 'bg-amberCC-50' : '';
              return (
                <tr key={o.order_id} className={`border-b border-line-soft ${rowBg}`}>
                  <td className="py-3 font-display font-semibold text-ink tracking-[-0.01em]">
                    <span className="inline-flex items-center gap-1.5">
                      {o.has_incidence && (
                        <span
                          className="text-amberCC-700 text-[14px]"
                          title={o.incidence_note || 'Incidencia · pendiente de revisión'}
                        >
                          ⚠
                        </span>
                      )}
                      {o.buyer_name}
                    </span>
                  </td>
                  <td className="py-3 text-ink-700 text-[12.5px] hidden md:table-cell">
                    {o.buyer_email}
                  </td>
                  <td className="py-3 text-center text-ink-700">{o.quantity}</td>
                  <td className="py-3 text-right font-display font-semibold text-ink">
                    {formatEUR(o.total_amount)}
                  </td>
                  <td className="py-3 text-center">
                    <div className="inline-flex flex-col items-center gap-1">
                      <span
                        className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-pill text-[11.5px] font-bold whitespace-nowrap"
                        style={{ background: s.bg, color: s.text }}
                      >
                        <span className="w-1.5 h-1.5 rounded-full" style={{ background: s.dot }} />
                        {orderStatusLabel[o.status] || o.status}
                      </span>
                      {o.is_offline && (
                        <span className="font-mono text-[9.5px] uppercase tracking-[0.14em] text-ink-500">
                          · offline
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="py-3 text-right">
                    {o.is_offline ? (
                      <div className="inline-flex flex-col items-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => undoOffline(o.order_id)}
                          className="inline-block px-3 py-1.5 rounded-pill border-[1.5px] border-line-strong bg-paper text-ink-700 text-[12px] font-semibold hover:border-brand transition"
                          title="Deshacer marcar offline"
                        >
                          Deshacer
                        </button>
                        <AttachmentToggle
                          slug={slug}
                          token={token}
                          orderId={o.order_id}
                          count={o.attachment_count || 0}
                          isOffline={true}
                        />
                      </div>
                    ) : o.status === 'pending' ? (
                      <div className="inline-flex flex-col items-end gap-1.5">
                        {o.whatsapp_reminder_url ? (
                          <a
                            href={o.whatsapp_reminder_url}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-block px-3 py-1.5 rounded-pill border-[1.5px] border-olive bg-olive-50 text-olive-700 text-[12px] font-semibold hover:bg-[#D8E2C2] transition"
                            title="Mandarle un recordatorio por WhatsApp"
                          >
                            Recordar
                          </a>
                        ) : (
                          <span className="text-[12px] text-ink-400 italic">sin teléfono</span>
                        )}
                        <button
                          type="button"
                          onClick={() => markOffline(o.order_id)}
                          className="inline-block px-3 py-1.5 rounded-pill border-[1.5px] border-line-strong bg-paper text-ink-700 text-[12px] font-semibold hover:border-brand transition"
                          title="Marcar como pagado por Bizum manual o efectivo"
                        >
                          Pagó offline
                        </button>
                      </div>
                    ) : o.status === 'paid' && !o.has_incidence ? (
                      <div className="inline-flex flex-col items-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => markIncidence(o.order_id)}
                          className="inline-block px-2.5 py-1.5 rounded-pill border border-[#ECD9AE] bg-amberCC-50 text-amberCC-700 text-[11.5px] font-semibold hover:bg-amberCC hover:text-paper transition"
                          title="Marcar como incidencia para revisar después"
                        >
                          ⚠ Incidencia
                        </button>
                        <AttachmentToggle
                          slug={slug}
                          token={token}
                          orderId={o.order_id}
                          count={o.attachment_count || 0}
                          isOffline={!!o.is_offline}
                        />
                      </div>
                    ) : o.has_incidence ? (
                      <div className="inline-flex flex-col items-end gap-1.5">
                        <button
                          type="button"
                          onClick={() => clearIncidence(o.order_id)}
                          className="inline-block px-2.5 py-1.5 rounded-pill border border-olive bg-olive-50 text-olive-700 text-[11.5px] font-semibold hover:bg-olive hover:text-paper transition"
                          title="Marcar incidencia como resuelta"
                        >
                          Resolver
                        </button>
                        <AttachmentToggle
                          slug={slug}
                          token={token}
                          orderId={o.order_id}
                          count={o.attachment_count || 0}
                          isOffline={!!o.is_offline}
                        />
                      </div>
                    ) : (
                      <span className="text-[12px] text-ink-400">—</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <p className="mt-4 text-[12px] text-ink-500 leading-snug">
        · "Pagó offline" deja constancia de que cobró el organizador por Bizum
        manual, efectivo o transferencia — sin cobrarse comisión de plataforma.
        · "⚠ Incidencia" marca un pago para revisión (importe que no cuadra,
        duplicado, transferencia con concepto raro…) sin alterar su estado.
      </p>
    </Card>
  );
}


/**
 * Decide qué panel mostrar según el procesador de pago configurado:
 *   - "pagafacil" · StripeConnectPanel + opción "Tengo TPV propio"
 *   - "redsys_org" · panel TPV propio + opción "Volver a Pagafácil Pay"
 *
 * El TPV propio (modo experto) exime al cobro de comisión transaccional.
 * Pagafácil cobra entonces solo por suscripción Premium.
 */
function PaymentProcessorSection({ slug, token }: { slug: string; token: string }) {
  const [config, setConfig] = useState<any>(null);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const refresh = useCallback(() => {
    api.get(`/public/group-buys/admin/${slug}/payment-config?t=${token}`)
      .then((r) => setConfig(r.data))
      .catch(() => setConfig({ provider: 'pagafacil', has_secret_key: false }));
  }, [slug, token]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!config) return null;

  if (config.provider === 'redsys_org') {
    return (
      <RedsysOrgActivePanel
        slug={slug}
        token={token}
        config={config}
        onReverted={refresh}
      />
    );
  }

  if (config.provider === 'monei') {
    return (
      <>
        <MoneiConnectPanel slug={slug} token={token} accountId={config.monei_account_id} />
        <details className="bg-paper border border-line rounded-card p-5">
          <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 hover:text-ink">
            · Volver a Stripe Connect (legacy)
          </summary>
          <div className="pt-3">
            <button
              type="button"
              onClick={async () => {
                if (!confirm('¿Volver a Stripe? MONEI Connect quedará desvinculado de este cobro.')) return;
                await api.put(`/public/group-buys/admin/${slug}/payment-config?t=${token}`, { provider: 'pagafacil' });
                toast.success('Vuelto a Stripe');
                refresh();
              }}
              className="px-4 py-2 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 text-[13px] font-semibold hover:border-brand transition"
            >
              Cambiar a Stripe
            </button>
          </div>
        </details>
      </>
    );
  }

  return (
    <>
      <StripeConnectPanel slug={slug} token={token} />
      <details className="bg-paper border border-line rounded-card p-5">
        <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 hover:text-ink">
          · Pasar a MONEI Connect (recomendado · 1,34 % Bizum + EDE España)
        </summary>
        <MoneiSetupForm slug={slug} token={token} onSaved={refresh} />
      </details>
      <details
        className="bg-paper border border-line rounded-card p-5"
        open={showAdvanced}
        onToggle={(e) => setShowAdvanced((e.target as HTMLDetailsElement).open)}
      >
        <summary className="cursor-pointer font-mono text-[11px] uppercase tracking-[0.18em] text-ink-500 hover:text-ink">
          · Modo experto · usar mi TPV propio (Bizum/Tarjeta Redsys)
        </summary>
        <RedsysOrgSetupForm slug={slug} token={token} onSaved={refresh} />
      </details>
    </>
  );
}


function MoneiSetupForm({
  slug,
  token,
  onSaved,
}: {
  slug: string;
  token: string;
  onSaved: () => void;
}) {
  const [saving, setSaving] = useState(false);

  const activate = async () => {
    if (!confirm('¿Activar MONEI Connect para este cobro? Te llevará al onboarding KYC.')) return;
    setSaving(true);
    try {
      const { data } = await api.post(
        `/public/group-buys/admin/${slug}/monei/connect/setup?t=${token}`,
      );
      onSaved();
      if (data.onboarding_url) {
        window.location.href = data.onboarding_url;
      } else {
        toast.success('Activado · onboarding pendiente desde el dashboard MONEI');
      }
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No se pudo activar');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-4 space-y-3">
      <p className="text-[13px] text-ink-700">
        MONEI es una <strong>Entidad de Pago licenciada por el Banco de España</strong> (#6911) con Bizum nativo (1,34 % + 0,34 €) y modelo marketplace estilo Stripe Connect. Es el procesador <strong>recomendado</strong> para hermandades, AMPAs y clubes que cobran online en España.
      </p>
      <p className="text-[12px] text-ink-500">
        Al activar, MONEI te pedirá los datos KYC del organizador (CIF/NIF, IBAN, identidad). Los pagos siguen llegando a tu cuenta como antes — solo cambia la pasarela.
      </p>
      <button
        type="button"
        onClick={activate}
        disabled={saving}
        className="px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14px] shadow-btn hover:bg-brand-700 transition disabled:opacity-60"
      >
        {saving ? 'Activando…' : 'Activar MONEI Connect →'}
      </button>
    </div>
  );
}


function MoneiConnectPanel({
  slug,
  token,
  accountId,
}: {
  slug: string;
  token: string;
  accountId: string | null;
}) {
  const [status, setStatus] = useState<any>(null);

  const refresh = useCallback(() => {
    api.get(`/public/group-buys/admin/${slug}/monei/connect/status?t=${token}`)
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [slug, token]);

  useEffect(() => { refresh(); }, [refresh]);

  if (!status) return null;

  if (status.ready_for_payout) {
    return (
      <section className="bg-olive-50 border border-[#C7D5A8] rounded-card p-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-olive-700 mb-2">
          · Procesador · MONEI Connect
        </div>
        <h2 className="font-display font-semibold text-[20px] text-olive-700 tracking-[-0.01em] mb-1">
          ✓ Cuenta MONEI activa
        </h2>
        <p className="text-[13.5px] text-ink-700">
          Los pagos van directos a tu cuenta vía MONEI (Banco de España #6911) con Bizum, tarjeta, Apple Pay y Google Pay.
        </p>
        <p className="text-[12px] text-ink-500 mt-1">Cuenta · {accountId}</p>
      </section>
    );
  }

  return (
    <section className="bg-amberCC-50 border border-[#ECD9AE] rounded-card p-6">
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-amberCC-700 mb-2">
        · Procesador · MONEI Connect
      </div>
      <h2 className="font-display font-semibold text-[20px] text-amberCC-700 tracking-[-0.01em] mb-1">
        Onboarding pendiente
      </h2>
      <p className="text-[13.5px] text-ink-700 mb-4">
        Termina el KYC en MONEI para empezar a recibir pagos. Mientras tanto los pagos no se procesan.
      </p>
      <button
        type="button"
        onClick={async () => {
          const { data } = await api.post(`/public/group-buys/admin/${slug}/monei/connect/setup?t=${token}`);
          if (data.onboarding_url) window.location.href = data.onboarding_url;
        }}
        className="px-5 py-3 rounded-xl bg-amberCC text-paper font-semibold text-[14px] shadow-btn hover:bg-amberCC-700 transition"
      >
        Continuar onboarding →
      </button>
    </section>
  );
}


function RedsysOrgActivePanel({
  slug,
  token,
  config,
  onReverted,
}: {
  slug: string;
  token: string;
  config: any;
  onReverted: () => void;
}) {
  const [reverting, setReverting] = useState(false);

  const revertToPagafacil = async () => {
    if (!confirm('¿Volver a Pagafácil Pay? Se borrarán las credenciales del TPV propio guardadas.')) return;
    setReverting(true);
    try {
      await api.put(`/public/group-buys/admin/${slug}/payment-config?t=${token}`, {
        provider: 'pagafacil',
      });
      toast.success('Volviendo a Pagafácil Pay');
      onReverted();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Error cambiando procesador');
    } finally {
      setReverting(false);
    }
  };

  return (
    <section className="bg-olive-50 border border-[#C7D5A8] rounded-card p-6">
      <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-olive-700 mb-2">
        · Procesador de pago · modo experto
      </div>
      <h2 className="font-display font-semibold text-[20px] text-olive-700 tracking-[-0.01em] mb-1">
        ✓ Cobras a tu TPV
      </h2>
      <p className="text-[13.5px] text-ink-700 mb-3">
        Los pagos van directos a tu TPV de Redsys (comercio <code className="font-mono text-[12.5px]">{config.redsys_merchant_code}</code>, terminal <code className="font-mono text-[12.5px]">{config.redsys_terminal}</code>). Pagafácil <strong>no cobra comisión transaccional</strong> en este cobro.
      </p>
      <p className="text-[12.5px] text-ink-500 mb-4">
        Cuando se cierre el cobro, no hay transfer pendiente porque el dinero ya está en tu banco.
      </p>
      <button
        onClick={revertToPagafacil}
        disabled={reverting}
        className="px-4 py-2 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 text-[13px] font-semibold hover:border-brand transition disabled:opacity-60"
      >
        {reverting ? 'Cambiando…' : 'Volver a Pagafácil Pay'}
      </button>
    </section>
  );
}


function RedsysOrgSetupForm({
  slug,
  token,
  onSaved,
}: {
  slug: string;
  token: string;
  onSaved: () => void;
}) {
  const [merchantCode, setMerchantCode] = useState('');
  const [terminal, setTerminal] = useState('001');
  const [secretKey, setSecretKey] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (!merchantCode.trim() || !secretKey.trim()) {
      toast.error('Rellena código de comercio y clave secreta');
      return;
    }
    if (!confirm('¿Activar tu TPV propio? Los próximos pagos irán a tu cuenta sin comisión Pagafácil.')) return;
    setSaving(true);
    try {
      await api.put(`/public/group-buys/admin/${slug}/payment-config?t=${token}`, {
        provider: 'redsys_org',
        redsys_merchant_code: merchantCode.trim(),
        redsys_terminal: terminal.trim() || '001',
        redsys_secret_key: secretKey.trim(),
      });
      toast.success('TPV propio activado');
      setMerchantCode('');
      setSecretKey('');
      onSaved();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Error activando TPV propio');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="pt-4 space-y-4">
      <p className="text-[13px] text-ink-700">
        Si tienes contratado un TPV Redsys con tu banco (BBVA, Sabadell, Caixa…), introduce sus credenciales. Los pagos del cobro irán a tu cuenta y Pagafácil <strong>no cobra comisión transaccional</strong>.
      </p>
      <p className="text-[12px] text-ink-500">
        · Datos en tu contrato Redsys del banco. La clave secreta es la "Clave SHA-256" del comercio.
        <br />
        · Solo puedes activarlo si este cobro no tiene pagos confirmados todavía.
      </p>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <div>
          <label className="block text-[12px] font-mono uppercase tracking-[0.12em] text-ink-500 mb-1">Código comercio</label>
          <input
            type="text"
            value={merchantCode}
            onChange={(e) => setMerchantCode(e.target.value)}
            placeholder="999008881"
            className="w-full px-3 py-2 border-[1.5px] border-line rounded-xl text-[13.5px] bg-paper focus:border-brand outline-none font-mono"
          />
        </div>
        <div>
          <label className="block text-[12px] font-mono uppercase tracking-[0.12em] text-ink-500 mb-1">Terminal</label>
          <input
            type="text"
            value={terminal}
            onChange={(e) => setTerminal(e.target.value)}
            placeholder="001"
            className="w-full px-3 py-2 border-[1.5px] border-line rounded-xl text-[13.5px] bg-paper focus:border-brand outline-none font-mono"
          />
        </div>
        <div>
          <label className="block text-[12px] font-mono uppercase tracking-[0.12em] text-ink-500 mb-1">Clave SHA-256</label>
          <input
            type="password"
            value={secretKey}
            onChange={(e) => setSecretKey(e.target.value)}
            placeholder="sq7HjrUOBfKmC576ILgskD5srU870gJ7"
            className="w-full px-3 py-2 border-[1.5px] border-line rounded-xl text-[13.5px] bg-paper focus:border-brand outline-none font-mono"
            autoComplete="new-password"
          />
        </div>
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="px-5 py-3 rounded-xl bg-olive-700 text-paper font-semibold text-[14px] shadow-btn hover:bg-olive-700/90 transition disabled:opacity-60"
      >
        {saving ? 'Activando…' : 'Activar TPV propio →'}
      </button>
    </div>
  );
}


function StripeConnectPanel({ slug, token }: { slug: string; token: string }) {
  const [status, setStatus] = useState<any>(null);
  const [loading, setLoading] = useState(false);

  const refresh = useCallback(() => {
    api.get(`/public/group-buys/admin/${slug}/connect/status?t=${token}`)
      .then((r) => setStatus(r.data))
      .catch(() => {});
  }, [slug, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const setup = async () => {
    setLoading(true);
    try {
      const { data } = await api.post(`/public/group-buys/admin/${slug}/connect/setup?t=${token}`);
      window.location.href = data.onboarding_url;
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Error iniciando onboarding');
      setLoading(false);
    }
  };

  if (!status) return null;

  const ready = status.ready_for_payout;
  const inProgress = status.setup_done && !ready;

  if (ready) {
    return (
      <section className="bg-olive-50 border border-[#C7D5A8] rounded-card p-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-olive-700 mb-2">
          · Cobrar a tu cuenta
        </div>
        <h2 className="font-display font-semibold text-[20px] text-olive-700 tracking-[-0.01em] mb-1">
          ✓ Tu cuenta está lista
        </h2>
        <p className="text-[13.5px] text-ink-700">
          Cuando se cierre el cobro, el dinero se transferirá solo a tu cuenta.
          Tarda 2–7 días en llegar.
        </p>
      </section>
    );
  }

  if (inProgress) {
    return (
      <section className="bg-amberCC-50 border border-[#ECD9AE] rounded-card p-6">
        <div className="font-mono text-[11px] uppercase tracking-[0.18em] text-amberCC-700 mb-2">
          · Cobrar a tu cuenta
        </div>
        <h2 className="font-display font-semibold text-[20px] text-amberCC-700 tracking-[-0.01em] mb-1">
          Te falta terminar el alta
        </h2>
        <p className="text-[13.5px] text-ink-700 mb-4">
          Stripe necesita unos datos más para validar tu cuenta.
        </p>
        <button
          onClick={setup}
          disabled={loading}
          className="px-5 py-3 rounded-xl bg-amberCC text-paper font-semibold text-[14px] shadow-btn hover:bg-amberCC-700 transition disabled:opacity-60"
        >
          {loading ? 'Un momento…' : 'Continuar →'}
        </button>
      </section>
    );
  }

  return (
    <Card eyebrow="Cobrar a tu cuenta">
      <p className="text-[14px] text-ink-700 mb-4">
        Vincula tu cuenta bancaria con Stripe (tarda 5 minutos) y el dinero te
        llega automáticamente al cerrarse el cobro.
        <br />
        <span className="text-[12.5px] text-ink-500">
          Mientras no lo hagas, el dinero queda en custodia segura.
        </span>
      </p>
      <button
        onClick={setup}
        disabled={loading}
        className="px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14px] shadow-btn hover:bg-brand-700 transition disabled:opacity-60"
      >
        {loading ? 'Un momento…' : 'Vincular mi cuenta (5 min) →'}
      </button>
    </Card>
  );
}


function PorraResultForm({
  slug,
  token,
  onSettled,
}: {
  slug: string;
  token: string;
  onSettled: () => void;
}) {
  const [home, setHome] = useState(0);
  const [away, setAway] = useState(0);
  const [summary, setSummary] = useState<any>(null);
  const [submitting, setSubmitting] = useState(false);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!confirm(`Cerrar la porra con resultado ${home}-${away}? Esta acción reparte el pot.`)) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(
        `/public/group-buys/admin/${slug}/porra/result?t=${token}`,
        { home_score: home, away_score: away },
      );
      setSummary(data);
      toast.success('Porra cerrada y repartida');
      onSettled();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'Error al cerrar porra');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Card eyebrow="Cargar resultado y cerrar porra">
      {summary ? (
        <div className="bg-olive-50 border border-[#C7D5A8] rounded-xl p-5">
          <p className="font-display font-semibold text-[20px] text-olive-700 tracking-[-0.01em]">
            {summary.home_team} {summary.score} {summary.away_team}
          </p>
          <p className="text-[14px] text-ink-700 mt-3">
            <strong>{summary.winners.length}</strong> ganador(es) con{' '}
            {summary.top_points} pts:{' '}
            <span className="text-olive-700">
              {summary.winners.join(', ') || 'nadie acertó'}
            </span>
          </p>
          <p className="text-[14px] text-ink-700 mt-1">
            Pot total:{' '}
            <strong className="text-ink">{formatEUR(summary.pot)}</strong>
            {summary.winners.length > 0 && (
              <>
                {' '}· Por cabeza:{' '}
                <strong className="text-ink">{formatEUR(summary.winner_share)}</strong>
              </>
            )}
          </p>
        </div>
      ) : (
        <form onSubmit={submit} className="flex flex-wrap items-end gap-3">
          <div>
            <label className="cc-label">Local</label>
            <input
              type="number"
              min={0}
              value={home}
              onChange={(e) => setHome(parseInt(e.target.value) || 0)}
              className="cc-input w-24"
            />
          </div>
          <div className="font-display text-[24px] text-ink-400 pb-2">−</div>
          <div>
            <label className="cc-label">Visitante</label>
            <input
              type="number"
              min={0}
              value={away}
              onChange={(e) => setAway(parseInt(e.target.value) || 0)}
              className="cc-input w-24"
            />
          </div>
          <button
            type="submit"
            disabled={submitting}
            className="px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14px] shadow-btn hover:bg-brand-700 transition disabled:opacity-60"
          >
            {submitting ? 'Cerrando…' : 'Cerrar porra y repartir'}
          </button>
        </form>
      )}
      <Style />
    </Card>
  );
}


function PorraRankingPanel({ slug }: { slug: string }) {
  const [ranking, setRanking] = useState<any>(null);

  useEffect(() => {
    api.get(`/public/group-buys/${slug}/ranking`)
      .then((r) => setRanking(r.data))
      .catch(() => {});
  }, [slug]);

  if (!ranking) return null;

  return (
    <Card eyebrow="Ranking en vivo">
      {ranking.entries.length === 0 ? (
        <p className="text-[14px] text-ink-500 italic">Aún no hay predicciones.</p>
      ) : (
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 border-b border-line">
                <th className="text-left py-2 font-normal w-12">#</th>
                <th className="text-left py-2 font-normal">Participante</th>
                <th className="text-center py-2 font-normal">Predicción</th>
                <th className="text-right py-2 font-normal">Puntos</th>
              </tr>
            </thead>
            <tbody>
              {ranking.entries.map((e: any, i: number) => (
                <tr
                  key={i}
                  className={
                    i === 0 && ranking.is_settled
                      ? 'bg-amberCC-50 border-b border-line-soft'
                      : 'border-b border-line-soft'
                  }
                >
                  <td className="py-2 font-display font-bold text-ink">{e.rank}</td>
                  <td className="py-2 text-ink">{e.participant_name}</td>
                  <td className="py-2 text-center font-mono text-ink-700">{e.prediction}</td>
                  <td className="py-2 text-right font-display font-bold text-olive-700">
                    {e.points}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-[12.5px] text-ink-500 mt-3 text-right font-mono uppercase tracking-[0.12em]">
        Pot total ·{' '}
        <strong className="text-ink">{formatEUR(parseFloat(ranking.pot_total))}</strong>
      </p>
    </Card>
  );
}


function Style() {
  return (
    <style>{`
      .cc-input{
        width:100%;
        padding:11px 14px;
        border:1.5px solid #DDD3C4;
        border-radius:11px;
        background:#FFFFFF;
        font-family:inherit;
        font-size:14px;
        color:#26201A;
        outline:none;
        transition:border-color .15s ease;
      }
      .cc-input:focus{ border-color:#1C5D82; }
      .cc-input::placeholder{ color:#A89D8E; }
      .cc-label{
        display:block;
        font-family:"Hanken Grotesk",sans-serif;
        font-size:12px;
        font-weight:700;
        letter-spacing:.02em;
        color:#4A4239;
        margin-bottom:6px;
      }
    `}</style>
  );
}


// ─── Oficina Fácil · panel "Mis facturas" ──────────────────────────────

function OfficinaFacilHubCTA({ organizerEmail }: { organizerEmail: string }) {
  const [entity, setEntity] = useState<any>(null);

  useEffect(() => {
    if (!organizerEmail) return;
    api.post('/public/entities/lookup', { email: organizerEmail })
      .then((r) => {
        const autonomo = (r.data.items || []).find((e: any) =>
          e.admin_url?.includes('/entidad/'),
        );
        setEntity(autonomo || null);
      })
      .catch(() => setEntity(null));
  }, [organizerEmail]);

  return (
    <Card eyebrow="Tu oficina · más allá de las facturas">
      <p className="text-[14px] text-ink-700 mb-4">
        Oficina Fácil organiza tu trabajo de autónomo · agenda fiscal AEAT con
        plazos (Modelos 130, 303, 390, 347, 100), CRM de clientes, biblioteca
        de documentos, tareas y dashboard con IVA repercutido + IRPF retenido.
      </p>
      {entity ? (
        <a
          href={entity.admin_url}
          className="inline-block px-5 py-3 rounded-xl bg-brand text-paper font-semibold text-[14px] shadow-btn hover:bg-brand-700 transition"
        >
          Abrir mi oficina →
        </a>
      ) : (
        <p className="text-[12.5px] text-ink-500 italic">
          Tu oficina se está creando automáticamente · te llegará un email con el enlace.
        </p>
      )}
    </Card>
  );
}


function InvoicesPanel({ slug, token }: { slug: string; token: string }) {
  const [data, setData] = useState<any>(null);
  const [uploading, setUploading] = useState(false);
  const [showRectify, setShowRectify] = useState<string | null>(null);
  const [rectifyReason, setRectifyReason] = useState('');

  const refresh = useCallback(() => {
    api.get(`/public/group-buys/admin/${slug}/invoices?t=${token}`)
      .then((r) => setData(r.data))
      .catch(() => {});
  }, [slug, token]);

  useEffect(() => { refresh(); }, [refresh]);

  const uploadLogo = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append('file', file);
    try {
      await api.post(`/public/group-buys/admin/${slug}/issuer-logo?t=${token}`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      toast.success('Logo subido');
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No pudimos subir el logo');
    } finally {
      setUploading(false);
    }
  };

  const removeLogo = async () => {
    if (!confirm('Quitar el logo del emisor?')) return;
    try {
      await api.delete(`/public/group-buys/admin/${slug}/issuer-logo?t=${token}`);
      toast.success('Logo quitado');
    } catch {
      toast.error('No pudimos quitarlo');
    }
  };

  const doRectify = async (orderId: string) => {
    if (!rectifyReason.trim()) {
      toast.error('Pon un motivo de rectificación');
      return;
    }
    try {
      const { data: res } = await api.post(
        `/public/group-buys/admin/${slug}/invoices/${orderId}/rectify?t=${token}`,
        { reason: rectifyReason.trim() },
      );
      toast.success(`Rectificativa ${res.rectif_number} creada`);
      setShowRectify(null);
      setRectifyReason('');
      refresh();
    } catch (err: any) {
      toast.error(err.response?.data?.detail || 'No pudimos rectificar');
    }
  };

  if (!data) return null;

  return (
    <Card eyebrow="Oficina Fácil · mis facturas">
      {/* Cabecera con métricas */}
      <div className="flex flex-wrap items-baseline justify-between gap-3 mb-4">
        <div>
          <p className="font-display font-semibold text-[20px] text-ink tracking-[-0.01em]">
            {data.count} factura{data.count === 1 ? '' : 's'} emitida{data.count === 1 ? '' : 's'}
          </p>
          <p className="text-[12.5px] text-ink-500 mt-1">
            Total facturado:{' '}
            <strong className="text-olive-700 font-display">
              {formatEUR(data.total_billed)}
            </strong>{' '}
            · Serie {data.issuer.prefix}-{new Date().getFullYear()}
          </p>
        </div>
        <div className="flex gap-2">
          <a
            href={`/api/public/group-buys/admin/${slug}/invoices.csv?t=${token}`}
            className="px-3 py-1.5 rounded-pill border-[1.5px] border-line-strong bg-paper text-ink-700 text-[12px] font-semibold hover:border-brand transition"
          >
            CSV
          </a>
          <a
            href={`/api/public/group-buys/admin/${slug}/invoices.zip?t=${token}`}
            className="px-3 py-1.5 rounded-pill border-[1.5px] border-line-strong bg-paper text-ink-700 text-[12px] font-semibold hover:border-brand transition"
          >
            ZIP de PDFs
          </a>
        </div>
      </div>

      {/* Logo */}
      <div className="mb-5 bg-surface border border-line rounded-xl p-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="text-[13.5px] text-ink-700">
          <strong className="text-ink">Logo del emisor</strong> · aparece en cada
          factura. Sube PNG, JPG o SVG · máx 200 KB.
        </div>
        <div className="flex gap-2">
          <label className="px-4 py-2 rounded-pill bg-brand text-paper text-[12.5px] font-semibold cursor-pointer hover:bg-brand-700 transition">
            {uploading ? 'Subiendo…' : 'Subir logo'}
            <input
              type="file"
              accept="image/png,image/jpeg,image/jpg,image/gif,image/svg+xml"
              onChange={uploadLogo}
              className="hidden"
              disabled={uploading}
            />
          </label>
          <button
            type="button"
            onClick={removeLogo}
            className="px-4 py-2 rounded-pill border-[1.5px] border-line-strong bg-paper text-ink-700 text-[12.5px] font-semibold hover:border-brick transition"
          >
            Quitar
          </button>
        </div>
      </div>

      {/* Tabla */}
      {data.invoices.length === 0 ? (
        <p className="text-[14px] text-ink-500 italic">
          Sin facturas emitidas todavía. Cuando un cliente pague, aparecerá aquí.
        </p>
      ) : (
        <div className="overflow-x-auto -mx-6 px-6">
          <table className="w-full text-[13.5px]">
            <thead>
              <tr className="font-mono text-[10.5px] uppercase tracking-[0.14em] text-ink-500 border-b border-line">
                <th className="text-left py-2 font-normal">Número</th>
                <th className="text-left py-2 font-normal">Fecha</th>
                <th className="text-left py-2 font-normal">Cliente</th>
                <th className="text-right py-2 font-normal">Total</th>
                <th className="text-right py-2 font-normal">Acción</th>
              </tr>
            </thead>
            <tbody>
              {data.invoices.map((inv: any) => (
                <tr key={inv.order_id} className="border-b border-line-soft align-top">
                  <td className="py-3 font-mono font-semibold text-brand-700">
                    {inv.invoice_number || '—'}
                    {inv.is_rectified && (
                      <span className="ml-2 px-2 py-0.5 rounded-pill bg-brick-50 text-brick-700 text-[10.5px] font-bold uppercase tracking-[0.06em]">
                        rectificada
                      </span>
                    )}
                  </td>
                  <td className="py-3 text-ink-700">
                    {new Date(inv.date).toLocaleDateString('es-ES', {
                      day: 'numeric',
                      month: 'short',
                      year: 'numeric',
                    })}
                  </td>
                  <td className="py-3">
                    <div className="font-display font-semibold text-ink tracking-[-0.01em]">
                      {inv.client_name}
                    </div>
                    <div className="text-[12px] text-ink-500">{inv.client_email}</div>
                  </td>
                  <td className="py-3 text-right font-display font-semibold text-ink tabular-nums">
                    {formatEUR(inv.total)}
                  </td>
                  <td className="py-3 text-right">
                    <div className="inline-flex flex-col items-end gap-1.5">
                      <a
                        href={inv.pdf_url}
                        className="inline-block px-3 py-1.5 rounded-pill border-[1.5px] border-brand bg-brand-50 text-brand-700 text-[12px] font-semibold hover:bg-brand hover:text-paper transition"
                      >
                        PDF
                      </a>
                      {!inv.is_rectified && (
                        <button
                          type="button"
                          onClick={() => {
                            setShowRectify(inv.order_id);
                            setRectifyReason('');
                          }}
                          className="inline-block px-3 py-1.5 rounded-pill border-[1.5px] border-line-strong bg-paper text-ink-700 text-[12px] font-semibold hover:border-brick transition"
                        >
                          Rectificar
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal rectificativa inline */}
      {showRectify && (
        <div className="mt-5 bg-brick-50 border border-[#E6B8B0] rounded-xl p-4">
          <div className="font-mono text-[10.5px] uppercase tracking-[0.18em] text-brick-700 mb-2">
            · Motivo de rectificación
          </div>
          <textarea
            value={rectifyReason}
            onChange={(e) => setRectifyReason(e.target.value)}
            rows={2}
            placeholder="Error en la cantidad facturada · devolución parcial · cliente equivocado"
            className="w-full p-3 rounded-lg border-[1.5px] border-line-strong bg-paper text-[14px] text-ink"
          />
          <div className="flex gap-2 mt-3">
            <button
              type="button"
              onClick={() => {
                setShowRectify(null);
                setRectifyReason('');
              }}
              className="px-4 py-2 rounded-xl border-[1.5px] border-line-strong bg-paper text-ink-700 text-[13px] font-semibold"
            >
              Cancelar
            </button>
            <button
              type="button"
              onClick={() => doRectify(showRectify)}
              className="flex-1 px-4 py-2 rounded-xl bg-brick text-paper text-[13px] font-semibold shadow-btn hover:bg-brick-700 transition"
            >
              Emitir rectificativa
            </button>
          </div>
        </div>
      )}

      <p className="mt-4 text-[12px] text-ink-500 leading-snug">
        · Las facturas se firman con cadena Verifactu (RD 1007/2023) · cada PDF
        incluye QR de verificación AEAT y huella SHA-256 enlazada con la anterior.
      </p>
    </Card>
  );
}
